import { FunctionComponent, useContext, useEffect, useState, useCallback, useRef, Dispatch, SetStateAction } from 'react'
import { IContactSupplierDetails, IPostContactSupplier, LookupActivityConfidence, ILookupActivityByNameResult } from '../../../../shared/services/contact-supplier/contact-supplier.model'
import { Box, Button, Chip, Link, SelectChangeEvent, Stack, Typography } from '@mui/material'
import SpendBasedConversionService from '../../../../shared/services/spend-based-conversion/spend-based-conversion.service';
import ContactSupplierService from '../../../../shared/services/contact-supplier/contact-supplier.service';
import { CompanyHousePicker } from '../../form-field-components/company-house-picker';
import { ICompanyHouseSearchResult } from '../../../../shared/services/company-house/company-house.model';
import { ISpendBasedConversion } from '../../../../shared/services/spend-based-conversion/spend-based-conversion.model';
import { useNavigate } from 'react-router-dom';
import { DialogSaveButtons } from '../../miscellaneous-components/dialog-save-buttons';
import { FormTextInputDisplay } from '../../form-field-components/new-and-edit/form-text-input';
import { FormAutoCompleteInputDisplay } from '../../form-field-components/new-and-edit/form-autocomplete-input';
import { FormCheckBoxInputDisplay } from '../../form-field-components/new-and-edit/form-checkbox-input';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import AccreditationsService from '../../../../shared/services/accreditations/accreditations.service';
import { IAccreditation } from '../../../../shared/services/accreditations/accreditations.model';
import CustomFieldService from '../../../../shared/services/custom-field/custom-field.service';
import { ICustomField } from '../../../../shared/services/custom-field/custom-field.model';
import ConversionSourceService from '../../../../shared/services/conversion-source/conversion-source.service';
import { IConversionSource } from '../../../../shared/services/conversion-source/conversion-source.model';
import { FormSelectInputDisplay } from '../../form-field-components/new-and-edit/form-select-input';
import moment from 'moment';
import { createDisplayFormatSpendBasedConversion, sortByConversionCode } from '../../../../shared/utils/conversion-utils';

export const SupplierForm: FunctionComponent<{
  isEditPage?: boolean,
  submitHandler: (values: IPostContactSupplier) => void,
  newSupplier: IPostContactSupplier,
  supplier?: IContactSupplierDetails
  isSubmitting?: boolean | undefined,
  supplierActive?: boolean,
  errorMessage?: string,
  errorCode?: number,
  editField?: string,
  setEditField?: Dispatch<SetStateAction<string>>,
  setErrorMessage?: Dispatch<SetStateAction<string | undefined>>,
  setErrorCode?: Dispatch<SetStateAction<number | undefined>>,
}> = ({
  isEditPage = false,
  submitHandler,
  newSupplier,
  supplier,
  isSubmitting,
  supplierActive,
  errorMessage,
  errorCode,
  editField,
  setEditField,
  setErrorMessage,
  setErrorCode,
}) => {
    const navigate = useNavigate()
    //Services
    const spendBasedConversionService = new SpendBasedConversionService();
    const accreditationsService = new AccreditationsService();
    const customFieldService = new CustomFieldService();
    const conversionSourceService = new ConversionSourceService();
    const contactSupplierService = ContactSupplierService.getInstance();
    //Context
    const screenLabelsContext = useContext(ScreenLabelsContext)
    //State
    const [conversionSources, setConversionSources] = useState<IConversionSource[]>()
    const [customFields, setCustomFields] = useState<ICustomField[]>();
    const [accreditations, setAccreditations] = useState<IAccreditation[]>()
    const [spendBasedConversions, setSpendBasedConversions] = useState<ISpendBasedConversion[]>()
    const [lookupLoading, setLookupLoading] = useState(false)
    const [lookupMessage, setLookupMessage] = useState<string | null>(null)
    const [showManualSearch, setShowManualSearch] = useState(false)
    const [lookupConfidence, setLookupConfidence] = useState<LookupActivityConfidence | null>(null)
    const confidenceChip: Record<LookupActivityConfidence, { label: string, color: 'success' | 'warning' | 'info' | 'default' }> = {
      HIGH: { label: 'High confidence', color: 'success' },
      MEDIUM: { label: 'Medium confidence', color: 'warning' },
      REVIEW: { label: 'Review', color: 'info' },
      NONE: { label: 'No match', color: 'default' },
    }
    //Use Effects
    useEffect(() => {
      conversionSourceService.getAll(true, 'spendBasedConversion')
        .then(response => {
          setConversionSources(response)
        })
        .catch(err => console.log(err))

      accreditationsService.getAll()
        .then(response => {
          setAccreditations(response)
        })
        .catch(err => console.log(err))

      customFieldService.getAllActive(`&entryType=contactSupplier`)
        .then(response => {
          setCustomFields(response)
        })
        .catch(err => {
          console.log(err)
        })
    }, [])

    const getInitialCustomFieldValues = useCallback(() => {
      if (!customFields || !customFields.length) return {};
      return customFields.reduce((acc, field) => {
        const matchingValue = isEditPage && 'customFieldValues' in newSupplier && newSupplier.customFieldValues
          ? newSupplier.customFieldValues.find(v => v.customFieldId === field.id)?.value ?? ''
          : '';
        acc[field.id] = matchingValue;
        return acc;
      }, {} as Record<string, any>);
    }, [customFields, isEditPage, newSupplier]);

    useEffect(() => {
      if (newSupplier) {
        let accreditationsArray: string[] = [];
        let otherText = '';
        if (supplier?.accreditationDetails && supplier.accreditationDetails.length > 0) {
          accreditationsArray = supplier.accreditationDetails.map(detail => detail.accreditationId);
          const otherDetail = supplier.accreditationDetails.find(detail => detail.accreditationId === 'd8d91c9c-e535-4709-b008-b6799ebf65ff');
          if (otherDetail?.customName) {
            otherText = otherDetail.customName;
          }
        } else {
          accreditationsArray = newSupplier.accreditations || [];
        }
        const customFieldValues = customFields && customFields.length ? getInitialCustomFieldValues() : {} as any;

        formikRef.current.setValues({
          name: newSupplier.name,
          spendBasedConversionId: newSupplier.spendBasedConversionId,
          defaultSpendBasedConversionSourceId: newSupplier.defaultSpendBasedConversionSourceId || '',
          companyHouseNumber: supplier?.companyHouseNumber ?? newSupplier.companyHouseNumber ?? null,
          primaryContactName: newSupplier.primaryContactName,
          primaryContactEmail: newSupplier.primaryContactEmail,
          contactSupplierReference: newSupplier.contactSupplierReference ? newSupplier.contactSupplierReference.split(',').join(', ') : null,
          accreditations: accreditationsArray,
          otherAccreditationText: otherText,
          haveCarbonReductionTargets: newSupplier.haveCarbonReductionTargets,
          targetsVerified: newSupplier.targetsVerified,
          ...customFieldValues,
        });
      }
    }, [customFields, newSupplier, supplier, isEditPage, getInitialCustomFieldValues])

    // The source whose activities the Default Activity field lists when the supplier stores no source
    // of its own. "Use subsidiary default" (null) is the norm, so without this the field was hidden
    // and the supplier's saved default activity was invisible. Resolved by the API for an existing
    // supplier: the source of the activity it holds, else the default subsidiary's source.
    const inheritedSpendSourceId: string = supplier?.effectiveSpendBasedConversionSourceId || ''

    //Formik
    const supplierSchema = Yup.object().shape({
      name: Yup.string().required(`You must provide a name for the ${screenLabelsContext.getLabel('supplier', { plural: false })}`),
      spendBasedConversionId: Yup.string().test('no-spend-based-without-source', 'You must select a conversion source before selecting an activity', function (value) {
        // An activity needs a source to belong to, but not an override on the supplier: "Use
        // subsidiary default" stores none, and the activity then sits under the inherited source.
        const defaultSpendBasedConversionSourceId = this.parent.defaultSpendBasedConversionSourceId || inheritedSpendSourceId;
        if (value && !defaultSpendBasedConversionSourceId) {
          return false;
        }
        return true;
      }).nullable(),
      defaultSpendBasedConversionSourceId: Yup.string().nullable(),
      primaryContactName: Yup.string().nullable(),
      primaryContactEmail: Yup.string().test('email-format', 'Please enter a valid email address', function (value) {
        if (!value || value.trim() === '') {
          return true;
        }
        return Yup.string().email().isValidSync(value);
      }).nullable(),
      contactSupplierReference: Yup.string().nullable(),
      haveCarbonReductionTargets: Yup.boolean(),
      targetsVerified: Yup.boolean(),
      accreditations: Yup.array().of(Yup.string()).when('targetsVerified', {
        is: true,
        then: (schema) => schema.test('at-least-one-accreditation', 'Please select at least one verifier', function (value) {
          return Array.isArray(value) && value.length > 0;
        }).required('Please select at least one verifier'),
        otherwise: (schema) => schema.nullable(),
      }),
      otherAccreditationText: Yup.string().when('accreditations', {
        is: (accreditations: string[]) => accreditations?.includes('d8d91c9c-e535-4709-b008-b6799ebf65ff'),
        then: (schema) => schema.required('Please specify the other verifier'),
        otherwise: (schema) => schema.nullable(),
      }),
      ...(customFields && customFields.length > 0 ? customFields.reduce((acc, field) => {
        if (field.isMandatory) {
          acc[field.id] = Yup.string().required(`${field.fieldName} is mandatory`);
        } else {
          acc[field.id] = Yup.string().nullable();
        }
        return acc;
      }, {} as any) : {}),
    })

    const formik = useFormik<any>({
      initialValues: {
        name: newSupplier.name,
        spendBasedConversionId: newSupplier.spendBasedConversionId,
        defaultSpendBasedConversionSourceId: newSupplier.defaultSpendBasedConversionSourceId || '',
        companyHouseNumber: newSupplier.companyHouseNumber ?? null,
        primaryContactName: newSupplier.primaryContactName,
        primaryContactEmail: newSupplier.primaryContactEmail,
        contactSupplierReference: newSupplier.contactSupplierReference ? newSupplier.contactSupplierReference.split(',').join(', ') : null,
        accreditations: newSupplier.accreditations,
        otherAccreditationText: '',
        haveCarbonReductionTargets: newSupplier.haveCarbonReductionTargets,
        targetsVerified: newSupplier.targetsVerified,
        ...getInitialCustomFieldValues(),
      },
      validationSchema: supplierSchema,
      onSubmit: (values) => {
        if (values.primaryContactEmail === '') {
          values.primaryContactEmail = null
        }
        if (values.primaryContactName === '') {
          values.primaryContactName = null
        }
        if (values.otherAccreditationText === '') {
          values.otherAccreditationText = null
        }
        if (values.defaultSpendBasedConversionSourceId === '') {
          values.defaultSpendBasedConversionSourceId = null
        }
        if (values.spendBasedConversionId === '') {
          values.spendBasedConversionId = null
        }
        const customFieldValues = Array.isArray(customFields)
          ? customFields.map((field) => ({
            customFieldId: field.id,
            value: values?.[field.id] ?? null,
          }))
          : []
        submitHandler({
          ...values,
          customFieldValues,
          suggestion: suggestionRef.current,
        })
      }
    });

    const formikRef = useRef(formik);
    formikRef.current = formik;

    // Holds what the classifier last suggested, sent with the save so the backend can
    // record suggested vs accepted (telemetry / training data). Null when the user set
    // the category by hand, so nothing is recorded in that case.
    const suggestionRef = useRef<any>(null);

    useEffect(() => {
      const sourceId = formik.values.defaultSpendBasedConversionSourceId || inheritedSpendSourceId
      if (isEditPage && sourceId) {
        spendBasedConversionService
          .getSpendBasedConversionsForConversionSource(sourceId, moment().year().toString(), true, true)
          .then((response: any) => {
            const rows = Array.isArray(response) ? response : response?.rows
            const formattedRows = (rows || []).map((conversion: ISpendBasedConversion) => createDisplayFormatSpendBasedConversion(conversion))
            setSpendBasedConversions(formattedRows)
          })
          .catch(err => console.log(err))
      }
    }, [isEditPage, formik.values.defaultSpendBasedConversionSourceId, inheritedSpendSourceId])

    //Formik Event Handlers
    const selectConversionHandler = (e: SelectChangeEvent<ISpendBasedConversion | null>, value: ISpendBasedConversion | null) => {
      if (value) {
        formik.setValues({ ...formik.values, spendBasedConversionId: value.id })
      }
    }

    const selectAccreditationsHandler = (e: any, value: IAccreditation[] | null) => {
      const accreditationIds = value ? value.map(accreditation => accreditation.id) : [];
      const hasOtherAccreditation = accreditationIds.includes('d8d91c9c-e535-4709-b008-b6799ebf65ff');
      const otherText = hasOtherAccreditation ? formik.values.otherAccreditationText : '';
      formik.setValues({
        ...formik.values,
        accreditations: accreditationIds,
        otherAccreditationText: otherText
      });
    }

    const targetsVerifiedHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = e.target.checked;
      formik.setValues({
        ...formik.values,
        targetsVerified: isChecked,
        accreditations: isChecked ? formik.values.accreditations : [],
        otherAccreditationText: isChecked ? formik.values.otherAccreditationText : ''
      });
    }

    const selectConversionSourceHandler = (e: SelectChangeEvent<string>) => {
      const conversionSourceId = e.target.value
      // Compare and load by the EFFECTIVE source: choosing "Use subsidiary default" stores no
      // override, and the activity list is then the inherited source's. The activity is cleared only
      // when the source it belongs to really changes.
      const previousSourceId = formik.values.defaultSpendBasedConversionSourceId || inheritedSpendSourceId
      const nextSourceId = conversionSourceId || inheritedSpendSourceId
      if (formik.values.spendBasedConversionId && previousSourceId && previousSourceId !== nextSourceId) {
        formik.setFieldValue('spendBasedConversionId', null)
      }
      formik.setFieldValue('defaultSpendBasedConversionSourceId', conversionSourceId)
      if (!nextSourceId) {
        setSpendBasedConversions(undefined)
        return
      }
      spendBasedConversionService.getSpendBasedConversionsForConversionSource(nextSourceId, moment().year().toString(), true, true)
        .then((response: any) => {
          const rows = Array.isArray(response) ? response : response?.rows
          const formattedRows = (rows || []).map((conversion: ISpendBasedConversion) => createDisplayFormatSpendBasedConversion(conversion))
          setSpendBasedConversions(formattedRows)
        })
        .catch(err => console.log(err))
    }

    // The suggested factor and the loaded options can come from different factor years, so fall back
    // to the loaded year's equivalent row rather than leaving the field with a value it cannot show.
    const matchLoadedConversionId = (loaded: ISpendBasedConversion[], result: ILookupActivityByNameResult): string | null => {
      if (loaded.some(conversion => conversion.id === result.spendBasedConversionId)) {
        return result.spendBasedConversionId
      }
      const sameCode = result.suggestedIdentifier
        ? loaded.find(conversion => conversion.localAuthorityCustomIdentifier === result.suggestedIdentifier)
        : undefined
      const sameActivity = result.suggestedActivity
        ? loaded.find(conversion => conversion.activity === result.suggestedActivity)
        : undefined
      return sameCode?.id ?? sameActivity?.id ?? result.spendBasedConversionId
    }

    // Shared: apply a lookup result (from name auto-match or a manual company pick) to the form.
    const applyLookupResult = async (result: ILookupActivityByNameResult): Promise<boolean> => {
      if (!result.sourceIsDefra) {
        setLookupConfidence(null)
        setLookupMessage('Automatic lookup uses UK Companies House data and is available for the DEFRA spend-based source only.')
        return false
      }
      if (result.matched && result.spendBasedConversionId && result.spendSourceId) {
        // Load the activity options for the resolved source, then set the fields for review.
        const response: any = await spendBasedConversionService.getSpendBasedConversionsForConversionSource(result.spendSourceId, moment().year().toString(), true, true)
        const rows = Array.isArray(response) ? response : response?.rows
        const formattedRows = (rows || []).map((conversion: ISpendBasedConversion) => createDisplayFormatSpendBasedConversion(conversion))
        setSpendBasedConversions(formattedRows)
        // The dropdown only holds one factor year, so if the suggestion is from another year, swap in
        // that year's equivalent row (same code, else same activity). Without this the field shows blank.
        const suggestedId = matchLoadedConversionId(formattedRows, result)
        formik.setValues({ ...formik.values, defaultSpendBasedConversionSourceId: result.spendSourceId, spendBasedConversionId: suggestedId, companyHouseNumber: result.matchedCompanyNumber ?? null })
        suggestionRef.current = {
          spendBasedConversionId: suggestedId,
          rawSicCode: (result as any).rawSicCode ?? result.suggestedIdentifier ?? null,
          confidence: result.confidence,
          matchType: (result as any).matchType ?? null,
        }
        const activityText = `${result.suggestedIdentifier ? `Code ${result.suggestedIdentifier}: ` : ''}${result.suggestedActivity ?? ''}`
        const matchedCo = result.matchedCompanyName
          ? `${result.matchedCompanyName}${result.matchedCompanyNumber ? ` (${result.matchedCompanyNumber})` : ''}`
          : 'a company'
        setLookupConfidence(result.confidence)
        setLookupMessage(`Suggested ${activityText}, matched to ${matchedCo}. Check this is the right company, then save to confirm.`)
        return true
      }
      setLookupConfidence(null)
      setLookupMessage('No spend factor was found for that company\'s classification.')
      return false
    }

    const lookUpDefaultActivityHandler = async () => {
      const name = (formik.values.name || '').trim()
      if (!name) {
        setLookupMessage('Enter a name first.')
        return
      }
      setLookupLoading(true)
      setLookupMessage(null)
      setLookupConfidence(null)
      try {
        const result = await contactSupplierService.previewLookupActivityByName(name)
        if (!result.matched && result.sourceIsDefra) {
          setLookupMessage('No confident match found for this name. Try searching Companies House.')
        } else {
          await applyLookupResult(result)
        }
      } catch (e) {
        setLookupMessage('Lookup failed. Please try again.')
      } finally {
        setLookupLoading(false)
      }
    }

    // Manual pick from the Companies House search.
    const pickCompanyHandler = async (company: ICompanyHouseSearchResult | null) => {
      if (!company) return
      setLookupLoading(true)
      setLookupMessage(null)
      setLookupConfidence(null)
      try {
        const result = await contactSupplierService.previewLookupActivityByCompany({
          companyNumber: company.companyNumber,
          sicCode: company.sicCode,
          companyName: company.companyName,
        })
        const applied = await applyLookupResult(result)
        if (applied) setShowManualSearch(false)
      } catch (e) {
        setLookupMessage('Lookup failed. Please try again.')
      } finally {
        setLookupLoading(false)
      }
    }

    const cancelEditHandler = () => {
      if (setEditField) {
        setEditField('')
      }
      setLookupMessage(null)
      setLookupConfidence(null)
      setShowManualSearch(false)
      formik.setTouched({})
      formik.setErrors({})
      if (setErrorMessage) {
        setErrorMessage(undefined)
      }
      if (setErrorCode) {
        setErrorCode(undefined)
      }
      if (newSupplier) {
        let accreditationsArray: string[] = [];
        let otherText = '';

        if (supplier?.accreditationDetails && supplier.accreditationDetails.length > 0) {
          accreditationsArray = supplier.accreditationDetails.map(detail => detail.accreditationId);
          const otherDetail = supplier.accreditationDetails.find(detail => detail.accreditationId === 'd8d91c9c-e535-4709-b008-b6799ebf65ff');
          if (otherDetail?.customName) {
            otherText = otherDetail.customName;
          }
        } else {
          accreditationsArray = newSupplier.accreditations || [];
        }

        const originalCustomFieldValues = getInitialCustomFieldValues();
        formik.setValues({
          name: newSupplier.name,
          spendBasedConversionId: newSupplier.spendBasedConversionId,
          defaultSpendBasedConversionSourceId: newSupplier.defaultSpendBasedConversionSourceId,
          primaryContactName: newSupplier.primaryContactName,
          primaryContactEmail: newSupplier.primaryContactEmail,
          haveCarbonReductionTargets: newSupplier.haveCarbonReductionTargets,
          contactSupplierReference: newSupplier.contactSupplierReference ? newSupplier.contactSupplierReference.split(',').join(', ') : null,
          accreditations: accreditationsArray,
          otherAccreditationText: otherText,
          targetsVerified: newSupplier.targetsVerified,
          ...originalCustomFieldValues,
        })
      }
    }

    // The lookup tools live on the add form always; on the edit page they appear only while the
    // spend-source field is being edited, so an existing supplier gets the same functionality.
    const editingSpendSource = isEditPage && editField === 'defaultSpendBasedConversionSourceId'
    const showLookupTools = !isEditPage || editingSpendSource

    return (
      <Box component='form' onSubmit={formik.handleSubmit}>
        <FormTextInputDisplay
          isTopRow={true}
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'name'}
          title='Name'
          name='name'
          value={formik.values.name}
          touched={formik.touched.name}
          errors={formik.errors.name}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          hideEditIcon={!supplierActive}
          customErrorMessage={errorCode === 1 ? errorMessage : undefined}
        />
        <FormTextInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'contactSupplierReference'}
          title={`${screenLabelsContext.getLabel('supplier', { plural: false, capitalize: 'first' })} Reference`}
          name='contactSupplierReference'
          value={formik.values.contactSupplierReference || ''}
          touched={formik.touched.contactSupplierReference}
          errors={formik.errors.contactSupplierReference}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          hideEditIcon={!supplierActive}
          tooltip='Enter multiple references separated by commas (e.g., REF1, REF2, REF3)'
        />
        <FormCheckBoxInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'haveCarbonReductionTargets'}
          title='Carbon Reduction Commitment'
          name='haveCarbonReductionTargets'
          label='Committed to science-based carbon reduction targets'
          value={formik.values.haveCarbonReductionTargets}
          touched={formik.touched.haveCarbonReductionTargets}
          errors={formik.errors.haveCarbonReductionTargets}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          hideEditIcon={!supplierActive}
        />
        <FormCheckBoxInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'targetsVerified'}
          title='Targets Verification'
          name='targetsVerified'
          label='Targets are third party verified or audited'
          value={formik.values.targetsVerified}
          touched={formik.touched.targetsVerified}
          errors={formik.errors.targetsVerified}
          onChange={targetsVerifiedHandler}
          cancelEditHandler={cancelEditHandler}
          hideEditIcon={!supplierActive}
          customErrorMessage={errorCode === 2 ? errorMessage : undefined}
        />
        {accreditations && formik.values.targetsVerified &&
          <FormAutoCompleteInputDisplay
            isEditPage={isEditPage}
            editField={editField}
            topBorder={false}
            setEditField={setEditField}
            disabled={isEditPage && editField !== 'targetsVerified'}
            title='Verifier'
            name='accreditations'
            forceOutlined={editField === 'targetsVerified'}
            value={formik.values.accreditations ? accreditations.filter(accreditation => formik.values.accreditations.includes(accreditation.id)) : []}
            touched={formik.touched.accreditations}
            errors={formik.errors.accreditations}
            onChange={selectAccreditationsHandler}
            cancelEditHandler={cancelEditHandler}
            hideEditIcon={true}
            options={accreditations.sort((a, b) => a.name.localeCompare(b.name))}
            optionLabelKey={'name'}
            multiple={true}
          />
        }
        {accreditations && formik.values.accreditations?.includes('d8d91c9c-e535-4709-b008-b6799ebf65ff') &&
          <FormTextInputDisplay
            isEditPage={isEditPage}
            editField={editField}
            setEditField={setEditField}
            topBorder={false}
            disabled={isEditPage && editField !== 'targetsVerified'}
            title='Other'
            forceOutlined={editField === 'targetsVerified'}
            name='otherAccreditationText'
            value={formik.values.otherAccreditationText}
            touched={formik.touched.otherAccreditationText}
            errors={formik.errors.otherAccreditationText}
            onChange={formik.handleChange}
            cancelEditHandler={cancelEditHandler}
            hideEditIcon={true}
            tooltip='Please specify the other verifier'
          />
        }
        {conversionSources &&
          <FormSelectInputDisplay
            isEditPage={isEditPage}
            editField={editField}
            setEditField={setEditField}
            disabled={isEditPage && editField !== 'defaultSpendBasedConversionSourceId'}
            title='Default Spend Conversion Factor Source'
            name='defaultSpendBasedConversionSourceId'
            value={formik.values.defaultSpendBasedConversionSourceId}
            touched={formik.touched.defaultSpendBasedConversionSourceId}
            errors={formik.errors.defaultSpendBasedConversionSourceId}
            onChange={selectConversionSourceHandler}
            cancelEditHandler={cancelEditHandler}
            hideEditIcon={!supplierActive}
            displayEmpty={true}
            tooltip={`On "Use ${screenLabelsContext.getLabel('subsidiary', { plural: false })} default" this supplier resolves its factor source from the ${screenLabelsContext.getLabel('subsidiary', { plural: false })} at entry time. Set a source here only to override that for this supplier.`}
            array={[
              { id: '', source: `Use ${screenLabelsContext.getLabel('subsidiary', { plural: false })} default` } as IConversionSource,
              ...conversionSources.sort((a, b) => a.source.localeCompare(b.source)),
            ]}
            labelKey="source"
            valueKey="id"
            endActions={!isEditPage ?
              <Button
                variant='contained'
                disableElevation
                disabled={lookupLoading || !formik.values.name}
                onClick={lookUpDefaultActivityHandler}
                sx={{ whiteSpace: 'nowrap', height: 40, px: 3 }}
              >
                {lookupLoading ? 'Looking up...' : 'Look up from Companies House'}
              </Button>
              : undefined}
          />}
        {showLookupTools &&
          <Box sx={{ borderLeft: '1px solid', borderRight: '1px solid', borderColor: 'lightGrey.main', pl: 3, pb: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Mirror the field's label(40%) + input(100%) columns so this lines up under the input. */}
            <Box sx={{ width: { xs: '100%', md: '40%' }, minWidth: { md: '100px' } }} />
            <Box sx={{ width: '100%', pl: { xs: 0, md: 2 } }}>
              {/* On the edit page the field's inline slot holds the save/cancel icons, so the Look up
                  button sits here under the source dropdown rather than beside it. */}
              {editingSpendSource &&
                <Box sx={{ mb: lookupMessage ? 1 : 1.5 }}>
                  <Button
                    variant='contained'
                    disableElevation
                    disabled={lookupLoading || !formik.values.name}
                    onClick={lookUpDefaultActivityHandler}
                    sx={{ whiteSpace: 'nowrap', height: 40, px: 3 }}
                  >
                    {lookupLoading ? 'Looking up...' : 'Look up from Companies House'}
                  </Button>
                </Box>}
              {lookupMessage &&
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                  {lookupConfidence && lookupConfidence !== 'NONE' &&
                    <Chip
                      size='small'
                      label={confidenceChip[lookupConfidence].label}
                      color={confidenceChip[lookupConfidence].color}
                      sx={{ height: 22, fontWeight: 600 }}
                    />}
                  <Typography variant='caption' color='text.secondary'>{lookupMessage}</Typography>
                </Box>}
              {!showManualSearch &&
                <Link component="button" type="button" variant="caption" onClick={() => setShowManualSearch(true)} sx={{ color: 'text.secondary' }}>
                  Or search Companies House manually
                </Link>}
              {showManualSearch &&
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <CompanyHousePicker
                      value={null}
                      onChange={pickCompanyHandler}
                      search={(term) => contactSupplierService.searchCompanyHouse(term)}
                      autoFocus
                    />
                  </Box>
                  {/* Hidden clone of the Look up button so the picker matches the dropdown's width above (add page only). */}
                  {!isEditPage &&
                    <Box sx={{ flexShrink: 0, ml: 1, mr: 3, display: 'flex', alignItems: 'center', visibility: 'hidden' }} aria-hidden>
                      <Button variant='contained' disableElevation sx={{ whiteSpace: 'nowrap', height: 40, px: 3 }}>
                        Look up from Companies House
                      </Button>
                    </Box>}
                </Box>}
            </Box>
          </Box>
        }
        {spendBasedConversions && (formik.values.defaultSpendBasedConversionSourceId || inheritedSpendSourceId) &&
          <FormAutoCompleteInputDisplay
            isEditPage={isEditPage}
            editField={editField}
            topBorder={false}
            setEditField={setEditField}
            disabled={isEditPage && editField !== 'defaultSpendBasedConversionSourceId'}
            title='Default Spend Activity'
            name='spendBasedConversionId'
            forceOutlined={editField === 'defaultSpendBasedConversionSourceId'}
            value={spendBasedConversions.find(conversion => conversion.id === formik.values.spendBasedConversionId) || null}
            touched={formik.touched.spendBasedConversionId}
            errors={formik.errors.spendBasedConversionId}
            onChange={(e: any, value: ISpendBasedConversion | null) => selectConversionHandler(e, value)}
            cancelEditHandler={cancelEditHandler}
            hideEditIcon={true}
            options={sortByConversionCode(spendBasedConversions)}
            optionLabelKey={'displayText'}
            optionText={''}
          />
        }
        {formik.values.companyHouseNumber &&
          <FormTextInputDisplay
            isEditPage={isEditPage}
            editField={editField}
            setEditField={setEditField}
            disabled={true}
            title='Companies House Number'
            name='companyHouseNumber'
            value={formik.values.companyHouseNumber || ''}
            touched={formik.touched.companyHouseNumber}
            errors={formik.errors.companyHouseNumber}
            onChange={formik.handleChange}
            cancelEditHandler={cancelEditHandler}
            hideEditIcon={true}
          />
        }
        <FormTextInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'primaryContactName'}
          title='Contact Name'
          name='primaryContactName'
          value={formik.values.primaryContactName || ''}
          touched={formik.touched.primaryContactName}
          errors={formik.errors.primaryContactName}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          hideEditIcon={!supplierActive}
        />
        <FormTextInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'primaryContactEmail'}
          title='Contact Email Address'
          name='primaryContactEmail'
          isBottomRow={customFields && customFields.length > 0 ? false : true}
          value={formik.values.primaryContactEmail || ''}
          touched={formik.touched.primaryContactEmail}
          errors={formik.errors.primaryContactEmail}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          hideEditIcon={!supplierActive}
          type='email'
        />
        {customFields && customFields.length > 0 && customFields.map((field, index) => (
          <FormTextInputDisplay
            key={field.id}
            isBottomRow={index === customFields.length - 1}
            isEditPage={isEditPage}
            editField={editField}
            setEditField={setEditField}
            disabled={isEditPage && editField !== field.id}
            title={field.fieldName}
            name={field.id}
            type={field.fieldType === 'number' ? 'number' as const : 'text' as const}
            value={formik.values[field.id]}
            touched={formik.touched[field.id]}
            errors={formik.errors[field.id]}
            onChange={formik.handleChange}
            cancelEditHandler={cancelEditHandler}
            hideEditIcon={!supplierActive}
          />
        ))
        }
        {!isEditPage &&
          <Stack direction='row' spacing={2} alignItems='center' justifyContent='flex-end' sx={{ mt: 3 }}>
            <DialogSaveButtons
              isForm={true}
              isSubmitting={isSubmitting}
              cancelHandler={() => navigate('/organisation/suppliers')}
              submitHandler={formik.submitForm}
            />
          </Stack>
        }
      </Box >
    )
  }